import { has, sortBy } from 'lodash';

import { getOrganizationProfileIfExist } from './organization';
import { CHANNEL_TYPE_INSTAGRAM } from './channels';
import { getExternalUrl } from './url';

import type { Entity, Image, InfluencerProfileLight, Organization, OrganizationWithEntity } from 'types';

export function getFullName(entity) {
  const firstName = entity.firstName || '';
  const lastName = entity.lastName || '';

  if (firstName.length === 0 && lastName.length === 0) {
    return 'Empty Name';
  }

  return `${firstName} ${lastName}`.trim();
}

export function getTruncatedName(entity) {
  const firstName = entity.firstName || '';
  const trimmedLastName = entity.lastName && entity.lastName.trim();
  const lastName = trimmedLastName ? `${trimmedLastName[0]}.` : '';

  if (firstName.length === 0 && lastName.length === 0) {
    return 'Empty Name';
  }

  return `${firstName} ${lastName}`;
}

export function getEntityFromUser(user) {
  const entity = {
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    displayName: ''
  };

  entity.displayName = getFullName(entity);

  return entity;
}

export const getContentCreatorProfileUrl = (id: number) => `/content-creator/profiles/${id}`;

export function getEntityFromInfluencerProfileLight(profile: InfluencerProfileLight): Entity | null {
  if (!profile) {
    return null;
  }

  return {
    id: profile.id,
    firstName: profile.name || '',
    lastName: '',
    image: profile.profileImage,
    url: profile.id ? getContentCreatorProfileUrl(profile.id) : '',
    displayName: profile.name
  };
}

export function getEntityFromProfile(profile: any, isAdmin?: boolean, omitLinks?: boolean): Entity | null {
  if (!profile) {
    return null;
  }

  let url = '';
  const isInfluencer = has(profile, 'influencerFirstName');
  if (isInfluencer) {
    url = profile.id ? getContentCreatorProfileUrl(profile.id) : '';
  } else {
    url = (profile as any).id ? `/brand/profiles/${(profile as any).id}` : '';
  }

  if (isAdmin) {
    url = `/admin${url}`;
  }

  if (omitLinks) {
    url = '';
  }

  const entity = {
    id: profile.id,
    firstName: profile.name || '',
    lastName: '',
    image: profile.profileImage,
    url,
    displayName: profile.name
  };

  if ((profile.influencerFirstName || '').length > 0 && (profile.influencerLastName || '').length > 0) {
    entity.firstName = profile.influencerFirstName;
    entity.lastName = profile.influencerLastName;
  }

  return entity;
}

export function getInstagramChannelEntity(influencerProfile) {
  const instagramChannel = influencerProfile.channels[CHANNEL_TYPE_INSTAGRAM] || {};
  const displayName = instagramChannel.username ? `@${instagramChannel.username}` : influencerProfile.name;
  return {
    id: influencerProfile.id,
    displayName,
    url: getExternalUrl(instagramChannel.url)
  };
}

export function getEntityFromOrganization(organization) {
  if (!organization) {
    return null;
  }

  const anyProfile = getOrganizationProfileIfExist(organization);
  const profileEntity = getEntityFromProfile(anyProfile);

  const entity = {
    id: organization.id,
    firstName: profileEntity ? profileEntity.displayName : organization.name,
    lastName: '',
    image: profileEntity ? profileEntity.image : undefined,
    displayName: ''
  };

  entity.displayName = getFullName(entity);

  return entity;
}

export function getEntityFromCampaign(campaign) {
  if (!campaign) {
    return null;
  }

  const mainImage: Image = campaign.mainImage;

  const entity = {
    id: campaign.id,
    firstName: campaign.name || '',
    lastName: '',
    image: mainImage ? { url: mainImage.url } : undefined,
    displayName: '',
    url: campaign.campaignType === 'ccm'
      ? `/campaigns/ccm/${campaign.id}/overview`
      : `/campaigns/${campaign.id}`
  };

  entity.displayName = getFullName(entity);

  return entity;
}

export function getEntityFromText(text) {
  if (!text) {
    return null;
  }

  const entity = {
    id: Math.random(),
    fallbackText: text,
    displayName: text
  };

  return entity;
}

export function getSortedOrganizationsWithEntities(organizations: Array<Organization>) {
  const organizationsWithEntities = (organizations || [])
    .map((organization) => {
      const entity = getEntityFromOrganization(organization);

      if (entity === null) {
        return null;
      }

      return {
        ...organization,
        entity
      };
    })
    .filter(val => val !== null) as Array<OrganizationWithEntity>;

  return sortBy(organizationsWithEntities, (o => o.entity.displayName?.toLowerCase()));
}

export function getOrganizationDisplayName(organization) {
  const entity = getEntityFromOrganization(organization);

  return entity ? entity.displayName : '';
}

export function getImageFallback(entity) {
  if (entity.fallbackText) {
    return entity.fallbackText;
  }

  let imageFallback = '';

  if (entity.firstName) {
    imageFallback += entity.firstName[0];
  }

  if (entity.lastName) {
    imageFallback += entity.lastName[0];
  }

  return imageFallback;
}
